/* The per-sheet records of a stage, rolled up into counts. */

const listOf = raw => {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  return null;
};

/* The two endpoints do not describe a row the same way, and the difference is not cosmetic. */
const extractionStatus = record => record?.latest_automation_task?.status;

const transformationStatus = record =>
  record?.task?.status === 'In progress' ? 'In progress' : record?.status;

const bucket = (records, statusOf) => {
  const list = listOf(records);
  if (!list) return null;
  const counts = { successful: 0, failed: 0, warned: 0, inProgress: 0, notCompleted: 0 };
  for (const record of list) {
    const status = statusOf(record);
    if (status === 'Completed') counts.successful += 1;
    else if (status === 'Failed' || status === 'Cancelled') counts.failed += 1;
    else if (status === 'Warned') counts.warned += 1;
    /* Queued is the server holding the work, not the work not existing. */
    else if (status === 'In progress' || status === 'Queued') counts.inProgress += 1;
    else counts.notCompleted += 1;
  }
  return counts;
};

/** Counts in the shape of `period.extraction_stats`, or null when there are no records to read. */
export const rollUpExtraction = records => {
  const counts = bucket(records, extractionStatus);
  if (!counts) return null;
  return {
    successful_extractions_number: counts.successful,
    failed_extractions_number: counts.failed,
    warned_extractions_number: counts.warned,
    in_progress_extractions_number: counts.inProgress,
    not_completed_extractions_number: counts.notCompleted
  };
};

/** Counts in the shape of `period.transformation_stats`, or null when there are no records. */
export const rollUpTransformation = records => {
  const counts = bucket(records, transformationStatus);
  if (!counts) return null;
  return {
    successful_transformations_number: counts.successful,
    failed_transformations_number: counts.failed,
    warned_transformations_number: counts.warned,
    in_progress_transformation_number: counts.inProgress,
    not_completed_transformations_number: counts.notCompleted
  };
};

/* The rows give the denominator; the period gives the first word that something is running. */
const withRunning = (rolled, own, field, touchedFields) => {
  /* Only while the rows have nothing to say at all. */
  const touched = touchedFields.reduce((sum, key) => sum + (rolled[key] || 0), 0);
  if (touched > 0) return rolled;
  const theirs = own?.[field] || 0;
  return theirs > 0 ? { ...rolled, [field]: theirs } : rolled;
};

export const withLiveStats = (period, { extractionRecords, transformationRecords } = {}) => {
  if (!period) return period;
  const extraction = rollUpExtraction(extractionRecords);
  const transformation = rollUpTransformation(transformationRecords);
  if (!extraction && !transformation) return period;
  return {
    ...period,
    ...(extraction
      ? {
          extraction_stats: withRunning(
            extraction,
            period.extraction_stats,
            'in_progress_extractions_number',
            [
              'successful_extractions_number',
              'failed_extractions_number',
              'warned_extractions_number',
              'in_progress_extractions_number'
            ]
          )
        }
      : null),
    ...(transformation
      ? {
          transformation_stats: withRunning(
            transformation,
            period.transformation_stats,
            'in_progress_transformation_number',
            [
              'successful_transformations_number',
              'failed_transformations_number',
              'warned_transformations_number',
              'in_progress_transformation_number'
            ]
          )
        }
      : null)
  };
};
